import { InputNumber } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UNIT_MEASURE } from '@common/constants';
import formatUOM from '@common/helpers/formatUOM';
import { ArrowInfo } from '@common/models';
import { handleDecimalNumberInput } from '@common/utils';

interface InputBoxCustomProps {
    arrowInfo: ArrowInfo;
    size: number;
    onUpdatedValue: (dimension: number, shapeId?: string) => void;
    shapeId?: string;
}

export const InputBoxCustom: React.FC<InputBoxCustomProps> = ({ arrowInfo, size, onUpdatedValue, shapeId }) => {
    const { t } = useTranslation();

    const [inputState, setInputState] = useState({
        dimension: 0,
        modifiedShapeId: undefined as string | undefined,
    });

    // Handle dimension updates from props
    useEffect(() => {
        const isShapeChanged = shapeId !== inputState.modifiedShapeId;
        const actualSize = isShapeChanged ? arrowInfo.size ?? size : inputState.dimension;

        setInputState((prev) => ({
            ...prev,
            dimension: actualSize,
        }));
    }, [arrowInfo.size, inputState.dimension, inputState.modifiedShapeId, shapeId, size]);

    const handleValueUpdate = useCallback(() => {
        const { dimension, modifiedShapeId } = inputState;
        if (modifiedShapeId) {
            setInputState((prev) => ({
                ...prev,
                modifiedShapeId: undefined,
            }));
            onUpdatedValue(dimension, modifiedShapeId);
        }
    }, [inputState, onUpdatedValue]);

    const handleInputChange = useCallback(
        (value: number | null) => {
            setInputState((prev) => ({
                dimension: value ?? prev.dimension,
                modifiedShapeId: shapeId,
            }));
        },
        [shapeId]
    );

    return (
        <div className="input-area">
            <div className="description">
                {t('SCROLLABLE_AREA.COUNTERTOP.LENGTH')}
                &nbsp; ({formatUOM(UNIT_MEASURE.SIDE_WALLS)})
            </div>
            <InputNumber
                value={inputState.dimension}
                onChange={handleInputChange}
                onKeyDown={handleDecimalNumberInput}
                onPressEnter={handleValueUpdate}
                onBlur={handleValueUpdate}
            />
        </div>
    );
};
